/**
 * ProofPage — the proof page's body: a hero over a sheet of pictures, the numbered sections of the product's spec and
 * a closing. It renders `ProofData` (`model.ts`), whose `view` carries the product's words, and decides nothing about
 * which pictures to show. The product passes its own header (and footer, where its layout does not carry one).
 */
import type {CSSProperties, ReactNode} from 'react'
import {Link} from 'react-router'
import type {Photo, ProofData, Rows, Section, Visual} from './model'
import {rowsSize} from './model'
import styles from './ProofPage.module.scss'

const frameStyle = (photo: Photo): CSSProperties | undefined =>
    photo.w && photo.h ? {aspectRatio: `${photo.w} / ${photo.h}`} : undefined

const Frame = ({photo}: {photo: Photo}) => {
    const img = <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" />
    return (
        <figure className={styles.ph} style={frameStyle(photo)}>
            {photo.to ? <Link className={styles.phLink} to={photo.to}>{img}</Link> : img}
        </figure>
    )
}

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20 L16 16" />
    </svg>
)

const Arrow = () => (
    <svg className={styles.arrow} width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12 H20" />
        <path d="M14 6 L20 12 L14 18" />
    </svg>
)

const PencilIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 3 a2.83 2.83 0 0 1 4 4 L7.5 20.5 L2 22 L3.5 16.5 Z" />
    </svg>
)

const Pill = ({text}: {text: string}) => (
    <span className={styles.pill}><PencilIcon />{text}</span>
)

function Demo({num, title, body, children}: {num: string; title: string; body: string | null; children: ReactNode}) {
    return (
        <div className={styles.demo} data-section={num}>
            <div className={styles.dtext}>
                <span className={styles.num}>{num}</span>
                <h3 className={styles.disp3}>{title}</h3>
                {body && <p className={styles.body}>{body}</p>}
            </div>
            <div className={styles.dvis}>{children}</div>
        </div>
    )
}

/** Without `rows` the mosaic fills the width; with it, whole rows at each width (`.rows`): a picture past the
 *  phone's rows is `display: none`, so its lazy image is never fetched. */
const Mosaic = ({photos, rows}: {photos: Photo[]; rows?: Rows}) => {
    if (!rows) {
        return (
            <div className={styles.mosaic}>
                {photos.map((photo, i) => <Frame key={`${photo.src}-${i}`} photo={photo} />)}
            </div>
        )
    }
    const narrowSize = rows.colsNarrow * rows.rowsNarrow
    const style = {'--cols': rows.cols, '--cols-narrow': rows.colsNarrow} as CSSProperties
    return (
        <div className={styles.rows} style={style}>
            {photos.slice(0, rowsSize(rows)).map((photo, i) => (
                <div key={`${photo.src}-${i}`} className={i >= narrowSize ? styles.beyondNarrow : undefined}>
                    <Frame photo={photo} />
                </div>
            ))}
        </div>
    )
}

/** What a product draws itself in a section, because the picture is its own interface: the armed wall bar, the
 *  in-progress chip. Each is a static, non-interactive render of the product's component. */
export type ProofParts = {
    armedBar?: (photo: Photo) => ReactNode
    building?: (visual: Extract<Visual, {kind: 'ownMuse'}>) => ReactNode
    /** Sessions the product plays with its own player; without it they are listed, unplayed. */
    sessions?: (visual: Extract<Visual, {kind: 'sessions' | 'listen'}>) => ReactNode
    /** The product's own screens in order, drawn still; without it the steps are a numbered list. */
    steps?: (visual: Extract<Visual, {kind: 'steps'}>) => ReactNode
}

function SessionList({visual, parts}: {visual: Extract<Visual, {kind: 'sessions' | 'listen'}>; parts: ProofParts}) {
    if (parts.sessions) return <>{parts.sessions(visual)}</>
    return (
        <ul className={styles.plainList}>
            {visual.sessions.map((session) => (
                <li key={session.id} lang={session.lang}>
                    <span className={styles.themeName}>{session.title}</span>
                    <span className={styles.body}>{session.writtenFor}</span>
                </li>
            ))}
        </ul>
    )
}

function SectionVisual({section, data, parts}: {section: Section; data: ProofData; parts: ProofParts}) {
    const {copy} = data.view
    const {visual} = section
    switch (visual.kind) {
        case 'catalogue':
            return <Mosaic photos={visual.photos} rows={visual.rows} />
        case 'search':
            return (
                <div className={styles.searchStack}>
                    <Link className={styles.field} to={`${copy.closing.to}?q=${encodeURIComponent(visual.query)}`}>
                        <SearchIcon />
                        <span>{visual.query}</span>
                    </Link>
                    <Mosaic photos={visual.results} rows={visual.rows} />
                </div>
            )
        case 'pair':
            return (
                <div className={styles.edits}>
                    {visual.pairs.map((pair) => (
                        <div key={pair.from.src} className={`${styles.pair} ${styles.pairFace}`}>
                            <div className={styles.pairItem}>
                                <Frame photo={pair.from} />
                                <span className={styles.tag}>{copy.tags.pairFrom}</span>
                            </div>
                            <Arrow />
                            <div className={styles.pairItem}>
                                <Frame photo={pair.withYou} />
                                <span className={styles.tag}>{copy.tags.pairWith}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )
        case 'edit':
            return (
                <div className={styles.edits}>
                    {visual.pairs.map((pair) => (
                        <div key={pair.before.src} className={`${styles.pair} ${styles.pairEdit}`}>
                            <div className={styles.pairItem}>
                                <Frame photo={pair.before} />
                                <span className={styles.tag}>{copy.tags.before}</span>
                            </div>
                            <div className={styles.sentence}>
                                <Pill text={pair.direction} />
                            </div>
                            <div className={styles.pairItem}>
                                <Frame photo={pair.after} />
                                <span className={styles.tag}>{copy.tags.after}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )
        case 'scratch':
            return (
                <div className={styles.searchStack}>
                    <div><Pill text={visual.direction} /></div>
                    <div className={styles.pairItem}>
                        <Frame photo={visual.photo} />
                    </div>
                </div>
            )
        case 'blank':
            return parts.armedBar ? <div className={styles.bar}>{parts.armedBar(visual.photo)}</div> : <Frame photo={visual.photo} />
        case 'themes':
            return (
                <div className={styles.searchStack}>
                    <div className={styles.themes}>
                        {visual.items.map((item) => (
                            <div key={item.label} className={styles.theme}>
                                <Frame photo={item.photo} />
                                <span className={styles.themeName}>{item.label}</span>
                                <span className={styles.tag}>{`${item.packs} ${copy.tags.packs}`}</span>
                            </div>
                        ))}
                    </div>
                    <span className={styles.tag}>
                        {`${visual.totals.packs.toLocaleString('en-US')} ${copy.tags.packs} · ${visual.totals.pictures.toLocaleString('en-US')} ${copy.tags.pictures}`}
                    </span>
                </div>
            )
        case 'neighbours':
            return (
                <div className={styles.neighbours}>
                    {[visual.picture, ...visual.neighbours].map((photo, i) => <Frame key={`${photo.src}-${i}`} photo={photo} />)}
                </div>
            )
        case 'muse':
            return (
                <div className={styles.searchStack}>
                    {visual.muses.map((muse) => (
                        <div key={muse.name} className={styles.museRow}>
                            <div className={styles.trio}>
                                {muse.scenes.map((photo, i) => <Frame key={`${photo.src}-${i}`} photo={photo} />)}
                            </div>
                            <span className={styles.tag}>{muse.name}</span>
                        </div>
                    ))}
                </div>
            )
        case 'sessions':
        case 'listen':
            return <SessionList visual={visual} parts={parts} />
        case 'steps':
            return parts.steps ? (
                <>{parts.steps(visual)}</>
            ) : (
                <ol className={styles.plainList}>
                    {visual.steps.map((step) => (
                        <li key={step.title}>
                            <span className={styles.themeName}>{step.title}</span>
                            <span className={styles.body}>{step.body}</span>
                        </li>
                    ))}
                </ol>
            )
        case 'note':
            return (
                <ul className={styles.plainList}>
                    {visual.lines.map((line) => <li key={line} className={styles.themeName}>{line}</li>)}
                </ul>
            )
        case 'links':
            return (
                <div className={styles.linkGrid}>
                    {visual.items.map((item) => (
                        <Link key={item.label} to={item.to} className={styles.linkCard}>
                            <span className={styles.themeName}>{item.label}</span>
                            <span className={styles.body}>{item.body}</span>
                        </Link>
                    ))}
                </div>
            )
        case 'ownMuse':
            return (
                <div className={styles.steps}>
                    <div className={styles.step}>
                        <div className={styles.six}>
                            {visual.photosIn.map((photo, i) => <Frame key={`${photo.src}-${i}`} photo={photo} />)}
                        </div>
                        <span className={styles.tag}>{section.steps[0]}</span>
                    </div>
                    <Arrow />
                    <div className={styles.step}>
                        {parts.building?.(visual)}
                        <span className={styles.tag}>{section.steps[1]}</span>
                    </div>
                    <Arrow />
                    <div className={styles.step}>
                        <div className={styles.trio}>
                            {visual.her.map((photo, i) => <Frame key={`${photo.src}-${i}`} photo={photo} />)}
                        </div>
                        <span className={styles.tag}>{section.steps[2]}</span>
                    </div>
                </div>
            )
    }
}

/** The page below the hero: the numbered sections, the chips and the closing. A product that sets its own lead (a panel
 *  standing where the hero would) draws this alone. `onClosing`, when given, makes the closing's link a button that calls it
 *  instead of a link that navigates; the closing keeps its words. */
export function ProofBody({data, parts = {}, onClosing}: {data: ProofData; parts?: ProofParts; onClosing?: () => void}) {
    const {sections, chips, view} = data
    const {copy} = view
    return (
        <>
            <section className={styles.demos}>
                <div className={`${styles.wrap} ${styles.demosHead}`}>
                    {copy.demosHeading && <h2 className={styles.disp2}>{copy.demosHeading}</h2>}
                    {sections.map((section, i) => (
                        <Demo key={section.kind} num={String(i + 1).padStart(2, '0')} title={section.title} body={section.body}>
                            <SectionVisual section={section} data={data} parts={parts} />
                        </Demo>
                    ))}
                </div>
            </section>

            {copy.chipsHeading && chips.length > 0 && (
                <section className={`${styles.demos} ${styles.demosEnd}`}>
                    <div className={`${styles.wrap} ${styles.chipsBlock}`}>
                        <h3 className={styles.disp3}>{copy.chipsHeading}</h3>
                        <div className={styles.cards}>
                            {chips.map(({label, slug, photo}) => (
                                <Link key={slug} to={`/${slug}`} className={styles.card}>
                                    <Frame photo={photo} />
                                    <span className={styles.themeName}>{label}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className={`${styles.wrap} ${styles.closing}`}>
                {copy.closing.title && <h2 className={styles.disp2}>{copy.closing.title}</h2>}
                {copy.closing.body && <p className={styles.body}>{copy.closing.body}</p>}
                {copy.closing.price && <p className={styles.body}>{copy.closing.price}</p>}
                {onClosing ? (
                    <button type="button" className={styles.verb} onClick={onClosing}>{`${copy.closing.link} →`}</button>
                ) : (
                    <Link className={styles.verb} to={copy.closing.to}>{`${copy.closing.link} →`}</Link>
                )}
            </section>
        </>
    )
}

/** The body inside another page: the proof's ground, type and colour, without the page's full-height frame. */
export function ProofSections(props: {data: ProofData; parts?: ProofParts; onClosing?: () => void}) {
    return (
        <div className={styles.inline}>
            <ProofBody {...props} />
        </div>
    )
}

export function ProofPage({data, header, footer, parts = {}}: {data: ProofData; header: ReactNode; footer?: ReactNode; parts?: ProofParts}) {
    const {sheet, hero, view} = data
    const {copy} = view
    const mixed = sheet.some((photo) => photo.w && photo.h && photo.w * 3 !== photo.h * 2)

    return (
        <div className={styles.page}>
            {header}

            <section className={`${styles.wrap} ${styles.hero}`}>
                <div className={styles.heroText}>
                    <h1 className={styles.disp1}>{copy.hero.title}</h1>
                    {copy.hero.line && <p className={styles.body}>{copy.hero.line}</p>}
                    {copy.hero.cta && <Link className={styles.verb} to={copy.hero.cta.to}>{copy.hero.cta.label}</Link>}
                </div>
                {hero ? (
                    <SessionList visual={hero} parts={parts} />
                ) : (
                    <div className={mixed ? `${styles.sheet} ${styles.sheetMixed}` : styles.sheet} data-sheet>
                        {sheet.map((photo, i) => <Frame key={`${photo.src}-${i}`} photo={photo} />)}
                    </div>
                )}
            </section>

            <ProofBody data={data} parts={parts} />
            {footer}
        </div>
    )
}
